/**
 * HR templated email notifications — same contract as Attendance leave/WFH emails.
 *
 * Attendance pattern (server-side, no frontend mail code):
 *   1. Frontend calls leave/approve or leave/reject API only.
 *   2. Backend loads `email_templates` row by `template_key`.
 *   3. Backend replaces `{{Placeholder}}` tokens and sends via mail-hr infra.
 *   4. CC/BCC from template `email_ids` column (comma-separated).
 *
 * NOTE: POST /api/mail-hr/templated returns 404 (endpoint not yet deployed).
 * All sends go through the local fallback: client-built HTML from
 * `email-templates.js` + POST /api/mail-hr (same as offer-letter flow).
 * Failures are logged only — never block the approval workflow.
 */
import mailService from './mail-service'
import emailTemplates from './email-templates'
import {
  REIMBURSEMENT_EMAIL_ENABLED,
  REIMBURSEMENT_EMAIL_CC,
} from '@/utils/reimbursement-config'

/** Known template keys — must match `email_templates.template_key` in DB. */
export const HR_EMAIL_TEMPLATE_KEYS = {
  REIMBURSEMENT_SUBMITTED: 'reimbursement_submitted',
  REIMBURSEMENT_APPROVAL_REQUIRED: 'reimbursement_approval_required',
  REIMBURSEMENT_LEVEL1_APPROVED: 'reimbursement_level1_approved',
  REIMBURSEMENT_LEVEL2_APPROVED: 'reimbursement_level2_approved',
  REIMBURSEMENT_FINAL_APPROVED: 'reimbursement_final_approved',
  REIMBURSEMENT_REJECTED: 'reimbursement_rejected',
  /** Task / Project Management module — see `src/services/task-service.js#notifyTaskAssigned`. */
  TASK_ASSIGNED: 'task_assigned',
  /** Due-date escalation tiers — see `src/services/task-service.js#notifyTaskEscalation` and `getEscalationLevel()` in `task-config.js`. */
  TASK_ESCALATION_1: 'task_escalation_1',
  TASK_ESCALATION_2: 'task_escalation_2',
  TASK_ESCALATION_FINAL: 'task_escalation_final',
  /** Gentle due-date reminder for recurring-task occurrences — no overdue escalation ladder. */
  TASK_REMINDER: 'task_reminder',
  /** Task marked Done — see `src/services/task-service.js#notifyTaskCompleted` (email to creator / PM / assignee when distinct from completer). Updates use push-only via `notifyTaskUpdated`. */
  TASK_UPDATED: 'task_updated',
  /**
   * Optional email for a manager ping — OFF by default since Ping is now
   * in-app (`task-ping-store.js` / `TaskBoard.vue#handlePingEmployee`).
   * Only sent when `notifyTaskPing({ sendEmail: true })` is called.
   * Body flexes via `{{PingMessage}}` (generic vs per-task).
   */
  TASK_PING: 'task_ping',
  /** Attendance-dashboard Announcement module — see `src/services/attendance-service.js#notifyAnnouncementPosted`. */
  ANNOUNCEMENT_POSTED: 'announcement_posted',
  /** Attendance-dashboard Company-Wide WFH Day module — see `src/services/attendance-service.js#notifyCompanyWfhDayAnnounced`. */
  COMPANY_WFH_DAY_ANNOUNCED: 'company_wfh_day_announced',
}

/**
 * Fixed company-wide distribution list that every "new announcement" email
 * goes to. Intentionally a hardcoded constant, NOT a config flag / env var
 * like `REIMBURSEMENT_EMAIL_CC` above — this was an explicit product
 * requirement ("send that mail to sattva-all@sattvahuman.com"), so it must
 * NOT become dynamic/configurable. If the distribution list address ever
 * changes, update this single constant.
 */
export const ANNOUNCEMENT_NOTIFICATION_RECIPIENT = 'sattva-all@sattvahuman.com'

/**
 * `emailTemplates.reimbursementMailByKey()` (called below) does not actually
 * exist on the `EmailTemplates` class in `email-templates.js` — there is no
 * key-driven lookup there, only named per-flow methods (`offerLetterEmail`,
 * `documentSubmittedMail`, ...). Calling it therefore throws, which
 * `_sendLocalFallback` previously did NOT guard against (the call sat
 * outside its own try/catch). Rather than grow the already-very-large
 * `email-templates.js` file, this small registry provides working,
 * self-contained HTML templates for the keys that need one and were never
 * wired up — right now just the Task module's assignment notice — while
 * leaving the reimbursement keys' lookup path guarded (typeof-checked
 * below) so a fix there, if one ever lands in `email-templates.js`, keeps
 * working unchanged.
 */
/**
 * Shared HTML shell (branded header bar + footer) wrapping every templated
 * email body in this file, so `TASK_ASSIGNED`, the 3 escalation tiers, and
 * `ANNOUNCEMENT_POSTED` all stay visually consistent instead of each being
 * a one-off `<div>`. Mirrors the header-bar / action-button / footer
 * conventions already used elsewhere in `email-templates.js` (see the
 * "Duplicacy Alert" template's `.header`/action-button/footer blocks)
 * rather than inventing a new style just for this module. `headerColor`
 * lets each template's banner match its own urgency (brand teal for a
 * plain assignment/announcement, escalating orange -> red -> dark red for
 * the 3 overdue tiers — same color-coding `TaskCard.vue`'s on-screen badge
 * uses) while the layout stays identical. `moduleLabel` overrides the
 * "Task Management" line in the header for non-Task modules (e.g.
 * "Announcements") without touching the Task templates, which omit it and
 * keep today's default.
 */
const SATTVA_TEAL = '#00AC99' // == `$sattva-p` in `_variables.scss` — same brand teal used for other transactional-email CTA buttons in `email-templates.js`.

function buildEmailShell({ headerColor, headerTitle, bodyHtml, moduleLabel = 'Tasks' }) {
  return '<div style="font-family: Arial, sans-serif; font-size: 14px; color: #333; line-height: 1.6; max-width: 600px; margin: 0 auto; background-color: #f5f5f5; padding: 20px;">'
    + '<div style="background-color: #ffffff; border-radius: 8px; overflow: hidden; box-shadow: 0 2px 4px rgba(0,0,0,0.08);">'
    + `<div style="background-color: ${headerColor}; color: #fff; padding: 16px 24px;">`
    + `<h2 style="margin: 0; font-size: 16px;">Sattva Human — ${moduleLabel}</h2>`
    + `<p style="margin: 4px 0 0 0; font-size: 13px; opacity: 0.9;">${headerTitle}</p>`
    + '</div>'
    + `<div style="padding: 24px;">${bodyHtml}</div>`
    + '<div style="text-align: center; padding: 16px 24px; border-top: 1px solid #eee; color: #888; font-size: 12px;">'
    + '<p style="margin: 0;">This is an automated notification — please do not reply to this email.</p>'
    + `<p style="margin: 4px 0 0 0;">For any queries, please contact <a href="mailto:connect@sattvahuman.com" style="color: ${SATTVA_TEAL}; text-decoration: none;">connect@sattvahuman.com</a></p>`
    + '</div>'
    + '</div>'
    + '</div>'
}

/** `{{TaskLink}}` is populated by `task-service.js#buildTaskDeepLink` — a real deep link to the task (falls back to the general Tasks page if the id isn't resolvable), not just "log in to the portal" prose. */
const TASK_CTA_BUTTON = '<div style="text-align: center; margin-top: 20px;">'
  + `<a href="{{TaskLink}}" target="_blank" style="display: inline-block; padding: 12px 28px; background-color: ${SATTVA_TEAL}; color: #fff; text-decoration: none; border-radius: 5px; font-weight: bold; font-size: 14px;">View Task →</a>`
  + '</div>'

/**
 * Deep link into the Attendance Dashboard's Announcement card, same
 * "hardcoded production domain + hash-route" convention as `TASKS_PAGE_URL`
 * in `task-service.js` — announcements have no dedicated detail page/id
 * (they're just a list on the dashboard), so this always points at the
 * dashboard itself rather than a per-item URL.
 */
const ANNOUNCEMENT_DASHBOARD_URL = 'https://ats.sattvahuman.in/#/dashboard/attendance'
const ANNOUNCEMENT_CTA_BUTTON = '<div style="text-align: center; margin-top: 20px;">'
  + `<a href="${ANNOUNCEMENT_DASHBOARD_URL}" target="_blank" style="display: inline-block; padding: 12px 28px; background-color: ${SATTVA_TEAL}; color: #fff; text-decoration: none; border-radius: 5px; font-weight: bold; font-size: 14px;">View Announcement →</a>`
  + '</div>'

/**
 * Same dashboard deep-link/CTA convention as `ANNOUNCEMENT_CTA_BUTTON` above —
 * Company-Wide WFH Days also have no dedicated detail page/id, just a card
 * on the Attendance Dashboard, so this points at the dashboard itself.
 */
const COMPANY_WFH_DAY_CTA_BUTTON = '<div style="text-align: center; margin-top: 20px;">'
  + `<a href="${ANNOUNCEMENT_DASHBOARD_URL}" target="_blank" style="display: inline-block; padding: 12px 28px; background-color: ${SATTVA_TEAL}; color: #fff; text-decoration: none; border-radius: 5px; font-weight: bold; font-size: 14px;">View on Attendance Dashboard →</a>`
  + '</div>'

/**
 * Shared body builder for the 3 escalation tiers below — same table shape
 * as `TASK_ASSIGNED`, plus a `DaysOverdue` row, an `AssignedBy` row for
 * manager context/urgency, and the color-coded banner whose urgency
 * (orange -> red -> dark red) mirrors the on-screen badge in
 * `TaskCard.vue`, so the email and the Kanban card tell the same story at
 * a glance. Wrapped in the same `buildEmailShell` as `TASK_ASSIGNED` for
 * visual consistency across all 4 templates.
 */
function buildEscalationHtml({ levelLabel, color, headerTitle }) {
  const body = `<p style="display:inline-block; padding:4px 10px; border-radius:4px; color:#fff; background-color:${color}; font-weight:bold;">${levelLabel} — OVERDUE TASK</p>`
    + '<p>Hi {{AssigneeName}},</p>'
    + `<p>The task below is now <strong>${levelLabel.toLowerCase()}</strong> — it is <strong>{{OverduePhrase}}</strong> and still not marked Done.</p>`
    + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
    + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:140px;">Title</td><td>{{TaskTitle}}</td></tr>'
    + '<tr><td style="font-weight:bold;">Status</td><td>{{TaskStatus}}</td></tr>'
    + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Priority</td><td>{{TaskPriority}}</td></tr>'
    + '<tr><td style="font-weight:bold;">Due Date</td><td>{{TaskDueDate}}</td></tr>'
    + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Days Overdue</td><td>{{DaysOverdue}}</td></tr>'
    + '<tr><td style="font-weight:bold;">Assigned To</td><td>{{AssigneeName}}</td></tr>'
    + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Assigned By</td><td>{{AssignedBy}}</td></tr>'
    + '</table>'
    + '<p>Please update its status or due date, or complete it as soon as possible.</p>'
    + TASK_CTA_BUTTON
  return buildEmailShell({ headerColor: color, headerTitle, bodyHtml: body })
}

const REIMBURSEMENT_CLAIM_TABLE = '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
  + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:160px;">Claim ID</td><td>{{ClaimId}}</td></tr>'
  + '<tr><td style="font-weight:bold;">Employee name</td><td>{{EmployeeName}}</td></tr>'
  + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Employee ID</td><td>{{EmployeeId}}</td></tr>'
  + '<tr><td style="font-weight:bold;">Submitted by</td><td>{{SubmittedBy}}</td></tr>'
  + '<tr><td style="font-weight:bold;">Item / Description</td><td>{{ItemDescription}}</td></tr>'
  + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Category</td><td>{{Category}}</td></tr>'
  + '<tr><td style="font-weight:bold;">Sub-category</td><td>{{SubCategory}}</td></tr>'
  + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Account</td><td>{{Account}}</td></tr>'
  + '<tr><td style="font-weight:bold;">Amount</td><td>{{Amount}}</td></tr>'
  + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Expense Date</td><td>{{ExpenseDate}}</td></tr>'
  + '<tr><td style="font-weight:bold;">Payment Mode</td><td>{{PaymentMode}}</td></tr>'
  + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Location</td><td>{{Location}}</td></tr>'
  + '<tr><td style="font-weight:bold;">Status</td><td>{{Status}}</td></tr>'
  + '</table>'

function buildReimbursementEmailShell({ headerTitle, bodyHtml }) {
  return buildEmailShell({
    headerColor: SATTVA_TEAL,
    headerTitle,
    moduleLabel: 'Reimbursements',
    bodyHtml,
  })
}

const GENERIC_TEMPLATES = {
  [HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_SUBMITTED]: {
    subject: 'Reimbursement Submitted — #{{ClaimId}}',
    html: buildReimbursementEmailShell({
      headerTitle: 'Reimbursement Submitted',
      bodyHtml: '<p>Hi {{SubmittedBy}},</p>'
        + '<p>A reimbursement claim for <strong>{{EmployeeName}}</strong> has been submitted successfully and is pending approval.</p>'
        + REIMBURSEMENT_CLAIM_TABLE,
    }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_APPROVAL_REQUIRED]: {
    subject: 'Reimbursement approval required — #{{ClaimId}} ({{ApprovalStage}})',
    html: buildReimbursementEmailShell({
      headerTitle: 'Approval Required',
      bodyHtml: '<p>Hi {{ManagerName}},</p>'
        + '<p>A reimbursement claim from <strong>{{EmployeeName}}</strong> requires your approval at <strong>{{ApprovalStage}}</strong>.</p>'
        + REIMBURSEMENT_CLAIM_TABLE
        + '<p style="margin-top:12px;"><strong>Comment:</strong> {{Comment}}</p>',
    }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_LEVEL1_APPROVED]: {
    subject: 'Reimbursement {{StageApproved}} — #{{ClaimId}}',
    html: buildReimbursementEmailShell({
      headerTitle: '{{StageApproved}}',
      bodyHtml: '<p>Hi {{EmployeeName}},</p>'
        + '<p>Your reimbursement claim has been <strong>{{StageApproved}}</strong> by <strong>{{ApprovedBy}}</strong>.</p>'
        + REIMBURSEMENT_CLAIM_TABLE
        + '<p style="margin-top:12px;"><strong>Remarks:</strong> {{Remarks}}</p>',
    }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_LEVEL2_APPROVED]: {
    subject: 'Reimbursement {{StageApproved}} — #{{ClaimId}}',
    html: buildReimbursementEmailShell({
      headerTitle: '{{StageApproved}}',
      bodyHtml: '<p>Hi {{EmployeeName}},</p>'
        + '<p>Your reimbursement claim has been <strong>{{StageApproved}}</strong> by <strong>{{ApprovedBy}}</strong>.</p>'
        + REIMBURSEMENT_CLAIM_TABLE
        + '<p style="margin-top:12px;"><strong>Remarks:</strong> {{Remarks}}</p>',
    }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_FINAL_APPROVED]: {
    subject: 'Reimbursement approved — #{{ClaimId}}',
    html: buildReimbursementEmailShell({
      headerTitle: 'Reimbursement Approved',
      bodyHtml: '<p>Hi {{EmployeeName}},</p>'
        + '<p>Your reimbursement claim has received final approval from <strong>{{ApprovedBy}}</strong>.</p>'
        + REIMBURSEMENT_CLAIM_TABLE
        + '<p style="margin-top:12px;"><strong>Remarks:</strong> {{Remarks}}</p>',
    }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.REIMBURSEMENT_REJECTED]: {
    subject: 'Reimbursement rejected — #{{ClaimId}}',
    html: buildReimbursementEmailShell({
      headerTitle: 'Reimbursement Rejected',
      bodyHtml: '<p>Hi {{EmployeeName}},</p>'
        + '<p>Your reimbursement claim was rejected at <strong>{{Stage}}</strong> by <strong>{{RejectedBy}}</strong>.</p>'
        + REIMBURSEMENT_CLAIM_TABLE
        + '<p style="margin-top:12px;"><strong>Rejection reason:</strong> {{RejectionComment}}</p>',
    }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.TASK_ASSIGNED]: {
    subject: 'New task assigned to you: {{TaskTitle}}',
    html: buildEmailShell({
      headerColor: SATTVA_TEAL,
      headerTitle: 'New Task Assigned',
      bodyHtml: '<p>Hi {{AssigneeName}},</p>'
        + '<p><strong>{{AssignedBy}}</strong> has assigned you a new task.</p>'
        + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:140px;">Title</td><td>{{TaskTitle}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Description</td><td>{{TaskDescription}}</td></tr>'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Priority</td><td>{{TaskPriority}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Due Date</td><td>{{TaskDueDate}}</td></tr>'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Assigned By</td><td>{{AssignedBy}}</td></tr>'
        + '</table>'
        + TASK_CTA_BUTTON,
    }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_1]: {
    subject: '[Escalation 1] Overdue task: {{TaskTitle}}',
    html: buildEscalationHtml({ levelLabel: 'Escalation 1', color: '#ff9f43', headerTitle: 'Escalation 1 — Overdue Task Reminder' }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_2]: {
    subject: '[Escalation 2] Overdue task: {{TaskTitle}}',
    html: buildEscalationHtml({ levelLabel: 'Escalation 2', color: '#ea5455', headerTitle: 'Escalation 2 — Overdue Task Reminder' }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_FINAL]: {
    subject: '[FINAL ESCALATION] Overdue task: {{TaskTitle}}',
    html: buildEscalationHtml({ levelLabel: 'FINAL ESCALATION', color: '#5e0000', headerTitle: 'Final Escalation — Immediate Action Required' }),
  },
  [HR_EMAIL_TEMPLATE_KEYS.TASK_REMINDER]: {
    subject: 'Reminder: {{TaskTitle}} is due',
    html: buildEmailShell({
      headerColor: SATTVA_TEAL,
      headerTitle: 'Recurring Task Reminder',
      bodyHtml: '<p>Hi {{AssigneeName}},</p>'
        + '<p>This is a friendly reminder about your recurring task below. Please complete it or update its status when done.</p>'
        + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:140px;">Title</td><td>{{TaskTitle}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Status</td><td>{{TaskStatus}}</td></tr>'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Priority</td><td>{{TaskPriority}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Due Date</td><td>{{TaskDueDate}}</td></tr>'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Assigned By</td><td>{{AssignedBy}}</td></tr>'
        + '</table>'
        + TASK_CTA_BUTTON,
    }),
  },
  /**
   * Sent on ANY change to an existing task — see
   * `src/services/task-service.js#notifyTaskUpdated`'s doc for the full
   * recipient/cc contract. `{{ChangesSummary}}` is a small pre-built HTML
   * table (one row per changed field, old value -> new value) generated by
   * `buildChangesSummaryHtml()` in `task-service.js` from the SAME
   * `buildHistoryEntries()` diff `TaskBoard.vue#recordTaskHistory` already
   * computes for the in-app History panel — never recomputed here.
   */
  [HR_EMAIL_TEMPLATE_KEYS.TASK_UPDATED]: {
    subject: 'Task updated: {{TaskTitle}}',
    html: buildEmailShell({
      headerColor: SATTVA_TEAL,
      headerTitle: 'Task Updated',
      bodyHtml: '<p>Hi {{RecipientName}},</p>'
        + '<p><strong>{{ChangedBy}}</strong> made changes to a task:</p>'
        + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:140px;">Title</td><td>{{TaskTitle}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Status</td><td>{{TaskStatus}}</td></tr>'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Priority</td><td>{{TaskPriority}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Due Date</td><td>{{TaskDueDate}}</td></tr>'
        + '</table>'
        + '<p style="font-weight:bold; margin-bottom:4px;">What changed:</p>'
        + '{{ChangesSummary}}'
        + TASK_CTA_BUTTON,
    }),
  },
  /**
   * A manager's manual "ping" — see `src/services/task-service.js#notifyTaskPing`,
   * `buildTaskPingEmailVariables()` (same file), `TaskListView.vue`'s
   * per-employee name-bell + per-row bell, and `TaskCard.vue`'s "Ping for
   * update" menu item. `{{PingMessage}}` is the ONE placeholder that
   * flexes between this template's two flavors — built entirely in
   * `buildTaskPingEmailVariables()`, never here:
   *   - Per-employee (no specific task attached): "pinged you regarding
   *     your tasks. Please review your Task Management board and update
   *     your progress." — the original, fully generic wording.
   *   - Per-task: "is asking for an update on: **[Task Title]** (Status:
   *     X, Due: Y). Please review it and update your progress." — and
   *     `{{TaskLink}}` (via `buildTaskDeepLink()`) points straight at that
   *     task instead of just the Tasks page.
   */
  [HR_EMAIL_TEMPLATE_KEYS.TASK_PING]: {
    subject: "You've been pinged by your manager",
    html: buildEmailShell({
      headerColor: SATTVA_TEAL,
      headerTitle: 'Manager Ping',
      bodyHtml: '<p>Hi {{EmployeeName}},</p>'
        + '<p><strong>{{ManagerName}}</strong> {{PingMessage}}</p>'
        + TASK_CTA_BUTTON,
    }),
  },
  /**
   * Sent to the fixed `ANNOUNCEMENT_NOTIFICATION_RECIPIENT` distribution
   * list (see const above) whenever a user with the `create`/`announcement`
   * ability publishes a new Announcement on the Attendance Dashboard — see
   * `src/services/attendance-service.js#notifyAnnouncementPosted` and its
   * call site in `AttendanceDashboard.vue#handleCreateAnnouncement`.
   */
  [HR_EMAIL_TEMPLATE_KEYS.ANNOUNCEMENT_POSTED]: {
    subject: 'New Announcement: {{AnnouncementTitle}}',
    html: buildEmailShell({
      headerColor: SATTVA_TEAL,
      headerTitle: 'New Announcement Posted',
      moduleLabel: 'Announcements',
      bodyHtml: '<p>Hi Team,</p>'
        + '<p><strong>{{PostedBy}}</strong> has posted a new announcement on the Attendance Dashboard.</p>'
        + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:140px;">Title</td><td>{{AnnouncementTitle}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Message</td><td>{{AnnouncementMessage}}</td></tr>'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Date</td><td>{{AnnouncementDate}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Posted By</td><td>{{PostedBy}}</td></tr>'
        + '</table>'
        + ANNOUNCEMENT_CTA_BUTTON,
    }),
  },
  /**
   * Sent to the fixed `ANNOUNCEMENT_NOTIFICATION_RECIPIENT` distribution
   * list (same company-wide list as `ANNOUNCEMENT_POSTED` — there is no
   * per-employee application step for a company-wide WFH day, so this
   * email is the only way everyone finds out) whenever a user with the
   * `create`/`company_wfh_day` ability declares a new Company-Wide WFH
   * Day on the Attendance Dashboard — see
   * `src/services/attendance-service.js#notifyCompanyWfhDayAnnounced` and
   * its call site in `AttendanceDashboard.vue#handleCreateCompanyWfhDay`.
   */
  [HR_EMAIL_TEMPLATE_KEYS.COMPANY_WFH_DAY_ANNOUNCED]: {
    subject: 'Company-Wide WFH Day announced: {{CompanyWfhDate}}',
    html: buildEmailShell({
      headerColor: SATTVA_TEAL,
      headerTitle: 'Company-Wide Work From Home Day',
      moduleLabel: 'Announcements',
      bodyHtml: '<p>Hi Team,</p>'
        + '<p><strong>{{PostedBy}}</strong> has declared a company-wide Work From Home day. '
        + 'No punch-in/punch-out is required on this date, and this does <strong>not</strong> '
        + 'deduct from anyone\'s personal WFH balance — it applies automatically to everyone covered.</p>'
        + '<table style="border-collapse: collapse; margin: 16px 0; width: 100%;" cellpadding="8">'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold; width:140px;">Date</td><td>{{CompanyWfhDate}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Applies To</td><td>{{CompanyWfhLocation}}</td></tr>'
        + '<tr style="background-color:#f8f9fa;"><td style="font-weight:bold;">Reason</td><td>{{CompanyWfhReason}}</td></tr>'
        + '<tr><td style="font-weight:bold;">Announced By</td><td>{{PostedBy}}</td></tr>'
        + '</table>'
        + COMPANY_WFH_DAY_CTA_BUTTON,
    }),
  },
}

/**
 * Template keys that get `REIMBURSEMENT_EMAIL_CC` (`hr@sattvahuman.com`)
 * automatically appended to their CC — see `sendTemplatedEmail()` below.
 * Deliberately an EXPLICIT allow-list, not "every template except Task
 * ones" — this is the reimbursement flow's own long-standing HR-visibility
 * requirement (see `REIMBURSEMENT_EMAIL_CC`'s doc in
 * `reimbursement-config.js`), which should NOT silently expand to catch
 * new template keys added later for unrelated modules.
 *
 * BUG FIX: this merge used to apply unconditionally to EVERY templated
 * send, including the Task module's `TASK_ASSIGNED`/`TASK_ESCALATION_*`
 * (and now `TASK_UPDATED`) emails — meaning every task email silently cc'd
 * HR regardless of what `cc` the caller actually passed in. Per explicit
 * product direction ("CC — NO HR email, only manager's email"), task
 * emails must NEVER be cc'd to HR; only the reimbursement flow's own
 * emails should still get this HR cc.
 */
const REIMBURSEMENT_HR_CC_TEMPLATE_KEYS = new Set([
  'reimbursement_submitted',
  'reimbursement_approval_required',
  'reimbursement_level1_approved',
  'reimbursement_level2_approved',
  'reimbursement_final_approved',
  'reimbursement_rejected',
])

/** Task Management module sender — must NOT use `hr@sattvahuman.com`. */
const TASK_EMAIL_FROM = 'connect@sattvahuman.com'
const TASK_EMAIL_FROM_NAME = 'Sattva Human — Tasks'

const TASK_EMAIL_TEMPLATE_KEYS = new Set([
  HR_EMAIL_TEMPLATE_KEYS.TASK_ASSIGNED,
  HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_1,
  HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_2,
  HR_EMAIL_TEMPLATE_KEYS.TASK_ESCALATION_FINAL,
  HR_EMAIL_TEMPLATE_KEYS.TASK_REMINDER,
  HR_EMAIL_TEMPLATE_KEYS.TASK_UPDATED,
  HR_EMAIL_TEMPLATE_KEYS.TASK_PING,
])

function buildGenericTemplatedMail(template_key, mailPayload) {
  const tpl = GENERIC_TEMPLATES[template_key]
  if (!tpl) return null
  const { to, cc, bcc, filename, ...variables } = mailPayload
  const mailData = {
    to,
    cc,
    bcc,
    subject: replaceTemplatePlaceholders(tpl.subject, variables),
    html: replaceTemplatePlaceholders(tpl.html, variables),
  }
  if (TASK_EMAIL_TEMPLATE_KEYS.has(template_key)) {
    mailData.fromMail = TASK_EMAIL_FROM
    mailData.fromName = TASK_EMAIL_FROM_NAME
  }
  // mail-hr attaches via getFileStream(S3 key). Omit empty/invalid keys —
  // a missing object makes POST /api/mail-hr return 500.
  if (filename) mailData.filename = filename
  return mailData
}

/**
 * Replace {{Key}} placeholders in subject/body (Attendance DB template style).
 * @param {string} text
 * @param {Object<string, string>} variables
 */
export function replaceTemplatePlaceholders(text, variables = {}) {
  if (!text) return ''
  return String(text).replace(/\{\{(\w+)\}\}/g, (match, key) => {
    const val = variables[key]
    return val != null && val !== '' ? String(val) : match
  })
}

/**
 * Normalize mailHR() result — API envelope or axios error wrapper.
 * Success envelope: { status: 'OK', data: { accepted, rejected } }
 */
export function normalizeMailHrResponse(res) {
  if (!res) return { envelope: null, delivery: null }

  const envelope = typeof res.status === 'number' && res.data && typeof res.data === 'object'
    ? res.data
    : res

  const delivery = envelope?.data && typeof envelope.data === 'object' && !Array.isArray(envelope.data)
    ? envelope.data
    : null

  return { envelope, delivery }
}

/** True when mail-hr reports OK or SMTP accepted recipients with none rejected. */
export function isMailHrSendSuccess(res) {
  if (!res) return false

  // mailService.mailHR() returns the API envelope directly — check top-level first.
  // Do NOT use res.data here; that is the nodemailer { accepted, rejected } payload.
  if (res.status === 'OK' || res.status === 200) return true

  const { envelope, delivery } = normalizeMailHrResponse(res)
  if (!envelope) return false

  if (
    envelope.status === 'OK'
    || envelope.status === 200
    || envelope.statusCode === 200
    || (typeof res.status === 'number' && res.status === 200)
  ) {
    return true
  }

  const accepted = delivery?.accepted
  const rejected = delivery?.rejected
  return Array.isArray(accepted)
    && accepted.length > 0
    && (!Array.isArray(rejected) || rejected.length === 0)
}

class HrEmailNotificationService {
  /**
   * Send a DB-backed templated HR email.
   * @returns {Promise<{status: string}>}
   */
  async sendTemplatedEmail({ template_key, to, cc = '', bcc = '', variables = {}, filename = '' }) {
    if (!REIMBURSEMENT_EMAIL_ENABLED) {
      return { status: 'SKIPPED' }
    }

    if (!to) {
      console.warn('[hr-email] skipped — no recipient for', template_key)
      return { status: 'SKIPPED' }
    }

    // See `REIMBURSEMENT_HR_CC_TEMPLATE_KEYS`'s doc just above — only the
    // reimbursement flow's own templates get the HR cc auto-merged in.
    // Every other template (Task module, Announcements, WFH days, ...)
    // sends exactly whatever `cc` its own caller passed — no HR address is
    // ever silently injected for those.
    const ccMerged = REIMBURSEMENT_HR_CC_TEMPLATE_KEYS.has(template_key)
      ? [cc, REIMBURSEMENT_EMAIL_CC].filter(Boolean).join(',').replace(/,,+/g, ',')
      : (cc || '')

    // POST /api/mail-hr/templated returns 404 — use local fallback directly.
    return this._sendLocalFallback({ template_key, to, cc: ccMerged, bcc, variables, filename })
  }

  async _sendLocalFallback({ template_key, to, cc, bcc, variables, filename }) {
    const mailPayload = { to, cc, bcc, ...variables }
    if (filename) mailPayload.filename = filename
    // Guarded: `reimbursementMailByKey` isn't actually defined on
    // `EmailTemplates` (see `GENERIC_TEMPLATES` comment above) — calling it
    // unconditionally would throw here, uncaught, since this line sits
    // before the try/catch below. Fall through to the generic registry
    // (currently just `TASK_ASSIGNED`) for any key it doesn't resolve.
    const mailData = (typeof emailTemplates.reimbursementMailByKey === 'function'
      ? emailTemplates.reimbursementMailByKey(template_key, mailPayload)
      : null) || buildGenericTemplatedMail(template_key, mailPayload)

    if (!mailData) {
      return { status: 'MOCK' }
    }

    if (filename && !mailData.filename) mailData.filename = filename

    try {
      const res = await mailService.mailHR(mailData)
      if (isMailHrSendSuccess(res)) {
        return { status: 'OK' }
      }
      const { envelope } = normalizeMailHrResponse(res)
      console.warn('[hr-email] mail-hr returned non-OK:', res)
      return { status: 'FAILED', detail: envelope || res }
    } catch (mailErr) {
      console.warn('[hr-email] mail-hr failed — would send:', template_key, to, mailErr.message || mailErr)
      return { status: 'FAILED' }
    }
  }

}

export default new HrEmailNotificationService()
